// Phase 4 — shared helpers, icons, constants, and the small TeamCrest
// component used by every flyer variant. Pulled out of Squads.jsx so the
// monolith stays manageable and each flyer is testable in isolation.

import React from "react";
import { SKILL_BY_CODE } from "../../data";

// ─── Player skill helpers ───────────────────────────────────────────────

export function playerSkillCodes(team, userId) {
  if (!team || !team.playerSkills) return [];
  const list = team.playerSkills[userId] || team.playerSkills[String(userId)];
  return Array.isArray(list) ? list : [];
}

// Cricket batting-order buckets for arranging the playing squad. Order:
// Top Order Batsmen → Wicket Keeper → Middle Order Batsmen → Pace →
// Medium Pace → Spin. All-rounders sit with the middle order. Players
// with no matching skill code drop to the end.
export const CRICKET_ORDER_BUCKETS = [
  ["OPENING_BAT", "TOP_ORDER_BAT"],
  ["WICKET_KEEPER"],
  ["MIDDLE_ORDER_BAT", "ALL_ROUNDER"],
  ["PACE"],
  ["MEDIUM_PACE"],
  ["OFF_SPIN", "LEG_SPIN"]
];

export function cricketBucketRank(skillCodes) {
  let best = CRICKET_ORDER_BUCKETS.length;
  for (const c of skillCodes) {
    const idx = CRICKET_ORDER_BUCKETS.findIndex(b => b.includes(c));
    if (idx >= 0 && idx < best) best = idx;
  }
  return best;
}

export function sortPlayersForSport(players, team, sport) {
  const byName = (a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  if (sport !== "Cricket") return [...players].sort(byName);
  return [...players].sort((a, b) => {
    const ra = cricketBucketRank(playerSkillCodes(team, a.id));
    const rb = cricketBucketRank(playerSkillCodes(team, b.id));
    return ra !== rb ? ra - rb : byName(a, b);
  });
}

// ─── Skill icons (used in flyer cards / roster lists) ───────────────────

export function BatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-label="bat">
      <rect x="10" y="2" width="4" height="6" rx="0.8" fill="#7a4a1c" stroke="#3a2410" strokeWidth="0.3" />
      <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="#3a2410" strokeWidth="0.2" />
      <line x1="10" y1="5"   x2="14" y2="5"   stroke="#3a2410" strokeWidth="0.2" />
      <line x1="10" y1="6.5" x2="14" y2="6.5" stroke="#3a2410" strokeWidth="0.2" />
      <path d="M 9 8 L 15 8 L 16.4 9.6 L 16.4 21 Q 16.4 22 15.4 22 L 8.6 22 Q 7.6 22 7.6 21 L 7.6 9.6 Z"
            fill="#f7c948" stroke="#b47a05" strokeWidth="0.4" />
      <line x1="8.6" y1="13" x2="15.4" y2="13" stroke="#b47a05" strokeWidth="0.3" opacity="0.6" />
      <line x1="8.6" y1="16" x2="15.4" y2="16" stroke="#b47a05" strokeWidth="0.3" opacity="0.6" />
    </svg>
  );
}

export function BallIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-label="ball">
      <circle cx="12" cy="12" r="9" fill="#ffffff" stroke="#333" strokeWidth="0.7" />
      <path d="M 3.4 12 Q 12 6.4 20.6 12" stroke="#c20000" strokeWidth="0.9" fill="none" />
      <path d="M 3.4 12 Q 12 6.4 20.6 12" stroke="#ffffff" strokeWidth="0.45" fill="none" strokeDasharray="0.6,1.2" />
      <ellipse cx="8.5" cy="9" rx="2" ry="1.2" fill="#fff" opacity="0.55" />
    </svg>
  );
}

export function StumpsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-label="stumps">
      <rect x="3.2" y="6.4"  width="8" height="1.6" rx="0.5" fill="#f7c948" stroke="#b47a05" strokeWidth="0.25" />
      <rect x="12.8" y="6.4" width="8" height="1.6" rx="0.5" fill="#f7c948" stroke="#b47a05" strokeWidth="0.25" />
      <rect x="4.5"  y="8" width="2" height="14" rx="0.6" fill="#f7c948" stroke="#b47a05" strokeWidth="0.3" />
      <rect x="11"   y="8" width="2" height="14" rx="0.6" fill="#f7c948" stroke="#b47a05" strokeWidth="0.3" />
      <rect x="17.5" y="8" width="2" height="14" rx="0.6" fill="#f7c948" stroke="#b47a05" strokeWidth="0.3" />
    </svg>
  );
}

export function categoryBadge(skillCode) {
  const s = SKILL_BY_CODE[skillCode];
  if (!s) return "?";
  if (s.category === "BAT")       return <BatIcon />;
  if (s.category === "BOWL_PACE") return <BallIcon />;
  if (s.category === "BOWL_SPIN") return <BallIcon />;
  if (s.category === "WK")        return <StumpsIcon />;
  if (s.category === "ALL")       return "AR";
  return "?";
}

// ─── Team logo / crest ──────────────────────────────────────────────────

// Per-team logo for the cricket flyer.
// Priority:
//   1. team.logoUrl        — admin-uploaded data URL via Admin → Flyer Designer
//   2. /rowdys-logo.png    — Rowdys Cricket Club branding
//   3. /gabruz-brand.png   — umbrella Gabruz Universe hero illustration
export function teamLogoSrc(team) {
  if (team?.logoUrl) return team.logoUrl;
  if (team?.code === "ROWDYS_CRICKET") return "/rowdys-logo.png";
  return "/gabruz-brand.png";
}

// Cricket flyer team mark — tries `teamLogoSrc` first; if that 404s
// (e.g. Rowdys logo hasn't been dropped in yet) falls back to a styled
// crest so the flyer is never broken-looking.
export function TeamCrest({ team, fallbackName, className = "flyerV2-logo" }) {
  const src = teamLogoSrc(team);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [src]);
  if (failed) {
    return (
      <div className={`${className} ${className}--fallback`} aria-label={fallbackName}>
        <span>{fallbackName}</span>
        <small>CRICKET CLUB</small>
      </div>
    );
  }
  return (
    <img
      className={className}
      src={src}
      alt={fallbackName}
      crossOrigin="anonymous"
      onError={() => setFailed(true)}
    />
  );
}

// ─── Squad rules ────────────────────────────────────────────────────────

export const SQUAD_RULES = {
  Cricket:    { startingSize: 12, maxSubs: 0, subLabel: "Substitute", playingLabel: "PLAYING SQUAD" },
  Volleyball: { startingSize: 6,  maxSubs: 3, subLabel: "Backup",     playingLabel: "PLAYING 6"     }
};

// Summer Volleyball is a dual-team format: pick TWO rosters (Team A +
// Team B), each with 8 starters and no backups. Resolved off team code
// rather than sport since plain Volleyball still uses the single-team rules.
export const TEAM_OVERRIDE_RULES = {
  SUMMER_VOLLEYBALL: {
    startingSize: 8,
    maxSubs: 0,
    subLabel: "Backup",
    playingLabel: "PLAYING 8",
    dualTeam: true,
    defaultVenue: "Blueberry Park"
  }
};

export const FALLBACK_RULES = { startingSize: 10, maxSubs: 0, subLabel: "Substitute", playingLabel: "PLAYING 10" };

export function rulesForSport(sport) { return SQUAD_RULES[sport] || FALLBACK_RULES; }

export function rulesForMatchAndTeam(match, team) {
  if (team?.code && TEAM_OVERRIDE_RULES[team.code]) return TEAM_OVERRIDE_RULES[team.code];
  return rulesForSport(match?.sport);
}

// ─── Formatting helpers ─────────────────────────────────────────────────

export const DEFAULT_CAPTAIN = "Let's start the season with a win — give it your all and have fun out there!";

export function defaultReportingTime(matchTime) {
  if (!matchTime) return "";
  const [h, m] = matchTime.split(":").map(Number);
  const total = h * 60 + m - 30;
  const safe = (total + 24 * 60) % (24 * 60);
  return `${String(Math.floor(safe/60)).padStart(2,"0")}:${String(safe%60).padStart(2,"0")}`;
}

export function formatDateTemplate(iso) {
  if (!iso) return "TBD";
  const d = new Date(`${iso}T00:00:00`);
  const day = d.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? "ST"
              : day % 10 === 2 && day !== 12 ? "ND"
              : day % 10 === 3 && day !== 13 ? "RD" : "TH";
  return `${d.toLocaleString("en-US",{month:"short"}).toUpperCase()} ${day}${suffix}`;
}

export function formatTimeTemplate(hhmm) {
  if (!hhmm) return "TBD";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = (h % 12) || 12;
  return m ? `${h12}:${String(m).padStart(2,"0")} ${period}` : `${h12}${period}`;
}

export function splitVenue(v) {
  if (!v) return { short: "TBD", address: "" };
  if (v.includes(":")) { const [s, ...r] = v.split(":"); return { short: s.trim(), address: r.join(":").trim() }; }
  if (v.includes(",")) { const p = v.split(","); return { short: p[0].trim(), address: p.slice(1).join(", ").trim() }; }
  return { short: v, address: "" };
}

// We store reporting time, opponent override, and the captain message in
// the squad's notes field (the schema doesn't have dedicated columns).
// "Footer:" is a back-compat key for squads saved before the rename.
// "Header:" used to populate a top ribbon on the flyer — that field was
// removed, so we silently drop any persisted Header: line on read.
export function parseSquadNotes(notes) {
  const out = {
    reportingTime: "", opponent: "",
    captain: DEFAULT_CAPTAIN,
    snacks: "", alcohol: "",
    // Phase 4 — per-squad captain + wicket-keeper picks. Stored as
    // numeric userIds; null when nothing picked yet (flyer falls back
    // to team-level captainId).
    captainPickId: null,
    wicketKeeperId: null
  };
  if (!notes) return out;
  for (const raw of notes.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith("Reporting time:"))   out.reportingTime = line.slice("Reporting time:".length).trim();
    else if (line.startsWith("Opponent override:")) out.opponent = line.slice("Opponent override:".length).trim();
    else if (line.startsWith("Captain pick:")) {
      const n = Number(line.slice("Captain pick:".length).trim());
      if (Number.isFinite(n) && n > 0) out.captainPickId = n;
    }
    else if (line.startsWith("Wicket Keeper:")) {
      const n = Number(line.slice("Wicket Keeper:".length).trim());
      if (Number.isFinite(n) && n > 0) out.wicketKeeperId = n;
    }
    else if (line.startsWith("Captain:"))     out.captain = line.slice("Captain:".length).trim();
    else if (line.startsWith("Footer:"))      out.captain = line.slice("Footer:".length).trim();
    else if (line.startsWith("Snacks:"))      out.snacks  = line.slice("Snacks:".length).trim();
    else if (line.startsWith("Alcohol:"))     out.alcohol = line.slice("Alcohol:".length).trim();
  }
  return out;
}
